import type { ReactNode } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { TOOLTIP_OVER_MODAL } from '../shared/ModalShell';

interface FieldLabelProps {
    htmlFor?: string;
    /** Marks the field mandatory with a red asterisk. */
    required?: boolean;
    /** Validation message, shown in red next to the asterisk. */
    error?: string;
    /** Explanation behind an info icon, shown on hover. */
    hint?: ReactNode;
    children: ReactNode;
}

// The fields also render inside modals, so the tooltip must clear their overlay.
function LabelHint({ hint }: { hint: ReactNode }) {
    return (
        <Tooltip content={hint} className={TOOLTIP_OVER_MODAL}>
            <span className="inline-flex text-gray-400 hover:text-gray-600" tabIndex={0}>
                <InformationCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
        </Tooltip>
    );
}

export function FieldLabel({ htmlFor, required = false, error, hint, children }: FieldLabelProps) {
    return (
        <div className="flex items-center gap-1 mb-1 min-w-0">
            <label htmlFor={htmlFor} className="text-xs font-semibold text-gray-700">
                {children}
                {required && (
                    <span className="ml-0.5 text-red-500" aria-hidden="true">
                        *
                    </span>
                )}
            </label>
            {hint && <LabelHint hint={hint} />}
            {error && (
                <span role="alert" className="ml-1 truncate text-xs text-red-500" title={error}>
                    {error}
                </span>
            )}
        </div>
    );
}
